import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  PersonaDependency,
  PersonaDependencyCreate,
  PersonasWithDependenciesResponse,
  ResourceSlot,
} from "src/types/personaDependencyTypes";

import axios from "axios";
import { nuclearn_api_url } from "src/App";

// Computed at call time (not module load) to avoid a temporal-dead-zone error:
// `nuclearn_api_url` is initialized in src/App, which may not have run yet when
// this module is first evaluated through a circular import.
const baseUrl = () => `${nuclearn_api_url}/chat`;

// API functions
const createDependency = (
  configId: number,
  data: PersonaDependencyCreate,
): Promise<PersonaDependency> => {
  return axios
    .post(`${baseUrl()}/config/${configId}/dependencies`, data)
    .then((response) => response.data);
};

const deleteDependency = (
  dependencyId: number,
): Promise<{ id: number; message: string }> => {
  return axios
    .delete(`${baseUrl()}/dependencies/${dependencyId}`)
    .then((response) => response.data);
};

const getAllPersonasWithDependencies =
  (): Promise<PersonasWithDependenciesResponse> => {
    return axios
      .get(`${baseUrl()}/config-with-dependencies`)
      .then((response) => {
        // Ensure the response has the expected structure
        const data = response.data;
        if (!data || typeof data !== "object") {
          throw new Error("Invalid response format from personas API");
        }
        return {
          personas: Array.isArray(data.personas) ? data.personas : [],
        };
      })
      .catch((error) => {
        console.error("Error fetching personas with dependencies:", error);
        throw error;
      });
  };

const getResourceSlots = (): Promise<ResourceSlot[]> => {
  return axios
    .get(`${nuclearn_api_url}/resource-management/slots`)
    .then((response) => {
      const data = response.data;
      if (!data || typeof data !== "object") {
        throw new Error("Invalid response format from resource slots API");
      }
      return Array.isArray(data.resource_slots) ? data.resource_slots : [];
    })
    .catch((error) => {
      console.error("Error fetching resource slots:", error);
      throw error;
    });
};

// React Query hooks
export const useGetAllPersonasWithDependencies = () => {
  return useQuery(
    ["personasWithDependencies"],
    () => getAllPersonasWithDependencies(),
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
    },
  );
};

export const useGetResourceSlots = () => {
  return useQuery(["resourceSlots"], () => getResourceSlots(), {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useCreateDependency = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ configId, data }: { configId: number; data: PersonaDependencyCreate }) =>
      createDependency(configId, data),
    {
      onSuccess: () => {
        // Invalidate and refetch personas with dependencies
        queryClient.invalidateQueries(["personasWithDependencies"]);
        console.log("Dependency created successfully");
      },
      onError: (error: any) => {
        console.error("Error creating dependency:", error.response);
      },
    },
  );
};

export const useDeleteDependency = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteDependency, {
    onSuccess: () => {
      // Invalidate and refetch personas with dependencies
      queryClient.invalidateQueries(["personasWithDependencies"]);
      queryClient.invalidateQueries(["personaDependencies"]);
      console.log("Dependency deleted successfully");
    },
    onError: (error: any) => {
      console.error("Error deleting dependency:", error.response);
    },
  });
};
